/**
 * Mietperioden (Plan 2.2, Festlegung 3): Buchung = Vertrag, Perioden = abrechenbare Einheiten.
 * Reine Funktionen auf Kalendertagen `YYYY-MM-DD` (Europe/Berlin), keine Uhrzeiten.
 *
 * Monatslogik (Etappe 3, Festlegung): ein Monat läuft vom Starttag bis zum Vortag desselben Tages im
 * Folgemonat (15.10.–14.11.). Fällt der Zieltag nicht in den Monat (31.), gilt das Monatsende.
 * Wochen sind 7 Tage.
 */

export type Einheit = "woche" | "monat";

export interface Periode {
  von: string;
  bis: string;
}

function teile(tag: string): [number, number, number] {
  const [j, m, t] = tag.split("-").map(Number);
  return [j!, m!, t!];
}

function utc(j: number, m: number, t: number): Date {
  return new Date(Date.UTC(j, m - 1, t));
}

export function tagAus(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function istTag(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [j, m, t] = teile(s);
  const d = utc(j, m, t);
  return d.getUTCFullYear() === j && d.getUTCMonth() === m - 1 && d.getUTCDate() === t;
}

export function tagPlus(tag: string, tage: number): string {
  const [j, m, t] = teile(tag);
  return tagAus(utc(j, m, t + tage));
}

/** Tage von `a` bis `b` (b − a), negativ wenn b vor a. */
export function tageZwischen(a: string, b: string): number {
  const [j1, m1, t1] = teile(a);
  const [j2, m2, t2] = teile(b);
  return Math.round((utc(j2, m2, t2).getTime() - utc(j1, m1, t1).getTime()) / 86_400_000);
}

export function letzterTagImMonat(j: number, m: number): number {
  return utc(j, m + 1, 0).getUTCDate();
}

/** Gleicher Tag `n` Monate später, auf das Monatsende geklammert (31.01. + 1 → 28.02.). */
export function monatPlus(tag: string, n: number): string {
  const [j, m, t] = teile(tag);
  const gesamt = (m - 1) + n;
  const jNeu = j + Math.floor(gesamt / 12);
  const mNeu = (((gesamt % 12) + 12) % 12) + 1;
  const tNeu = Math.min(t, letzterTagImMonat(jNeu, mNeu));
  return tagAus(utc(jNeu, mNeu, tNeu));
}

/** Letzter Tag einer Laufzeit von `dauer` Einheiten ab `start` (einschließlich). */
export function endeVon(start: string, einheit: Einheit, dauer: number): string {
  if (dauer < 1) throw new Error("Dauer muss mindestens 1 sein");
  return einheit === "woche" ? tagPlus(start, dauer * 7 - 1) : tagPlus(monatPlus(start, dauer), -1);
}

/** Perioden einer festen Laufzeit (eine je Einheit). */
export function periodenErzeugen(start: string, einheit: Einheit, dauer: number): Periode[] {
  const p: Periode[] = [];
  let von = start;
  for (let i = 1; i <= dauer; i++) {
    const bis = einheit === "woche" ? tagPlus(von, 6) : tagPlus(monatPlus(start, i), -1);
    p.push({ von, bis });
    von = tagPlus(bis, 1);
  }
  return p;
}

/**
 * Perioden eines Abos bis mindestens `bisTag` (einschließlich): nach den schon vorhandenen Perioden
 * werden weitere angehängt, bis `bisTag` abgedeckt ist. Liefert nur die neuen Perioden.
 */
export function aboPeriodenNachziehen(start: string, einheit: Einheit, vorhandene: number, bisTag: string, ende: string | null = null): Periode[] {
  const neu: Periode[] = [];
  let i = vorhandene;
  // Letzter abgedeckter Tag
  let abgedeckt = vorhandene === 0 ? tagPlus(start, -1) : einheit === "woche" ? tagPlus(start, vorhandene * 7 - 1) : tagPlus(monatPlus(start, vorhandene), -1);
  while (abgedeckt < bisTag && (!ende || abgedeckt < ende)) {
    const von = tagPlus(abgedeckt, 1);
    let bis = einheit === "woche" ? tagPlus(von, 6) : tagPlus(monatPlus(start, i + 1), -1);
    if (ende && bis > ende) bis = ende;
    neu.push({ von, bis });
    abgedeckt = bis;
    i++;
  }
  return neu;
}

/**
 * Frühestmögliches Kündigungsdatum eines Abos (Plan: „zum Monatsende“ mit Frist in Wochen):
 * das nächste Monatsende, das mindestens `fristWochen` Wochen nach `heute` liegt – nie vor `mindestEnde`
 * (Ende der ersten Periode).
 */
export function fruehestesKuendigungsende(heute: string, fristWochen: number, mindestEnde: string): string {
  const fruehestens = tagPlus(heute, fristWochen * 7);
  const [j, m] = teile(fruehestens);
  let kandidat = tagAus(utc(j, m, letzterTagImMonat(j, m)));
  if (kandidat < mindestEnde) kandidat = mindestEnde;
  return kandidat;
}

/** Anteiliger Betrag einer angebrochenen Periode (z. B. gekündigt vor Periodenende), halb aufrunden. */
export function anteiligCent(betragCent: number, periode: Periode, bis: string): number {
  const tage = tageZwischen(periode.von, periode.bis) + 1;
  const genutzt = Math.max(0, Math.min(tage, tageZwischen(periode.von, bis) + 1));
  return Math.round((betragCent * genutzt) / tage);
}

/** Überschneiden sich zwei Zeiträume (Ende null = offen)? */
export function ueberschneidet(aVon: string, aBis: string | null, bVon: string, bBis: string | null): boolean {
  return (aBis === null || aBis >= bVon) && (bBis === null || bBis >= aVon);
}
